/**
 * Auth Service
 * Handles all authentication-related API operations
 */
import * as api from '../api';
import type { CustomerData, LoginResponse } from '../api';

/**
 * Register a new user
 * @param email - User email
 * @param password - User password
 * @param displayName - Display name
 * @returns Login response with token and customer ID
 */
export async function register(email: string, password: string, displayName: string): Promise<LoginResponse> {
  return api.register(email, password, displayName);
}

/**
 * Login with email and password
 * @param email - User email
 * @param password - User password
 * @param rememberMe - Whether to persist the session
 * @returns Login response with token and customer ID
 */
export async function loginLocal(email: string, password: string, rememberMe: boolean = false): Promise<LoginResponse> {
  return api.loginLocal(email, password, rememberMe);
}

/**
 * Login with social provider (Google or Facebook)
 * Redirects the page to OAuth provider
 * @param provider - 'google' or 'facebook'
 * @param rememberMe - Whether to persist the session
 */
export async function loginSocial(provider: 'google' | 'facebook', rememberMe: boolean = false): Promise<void> {
  return api.loginSocial(provider, rememberMe);
}

/**
 * Log out the current user
 */
export async function logout(): Promise<void> {
  clearAuthToken();
  return api.logout();
}

/**
 * Fetch current customer's profile data
 */
export async function getCustomerProfile(): Promise<CustomerData> {
  return api.fetchCustomerData();
}

/**
 * Check if user is currently authenticated
 * @returns true if auth token exists
 */
export function isAuthenticated(): boolean {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  return !!token;
}

/**
 * Get the current auth token
 */
export function getAuthToken(): string | null {
  return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token') || null;
}

/**
 * Store auth token
 */
export function setAuthToken(token: string, rememberMe: boolean = false): void {
  if (rememberMe) {
    localStorage.setItem('auth_token', token);
  } else {
    sessionStorage.setItem('auth_token', token);
  }
}

/**
 * Clear auth token
 */
export function clearAuthToken(): void {
  localStorage.removeItem('auth_token');
  sessionStorage.removeItem('auth_token');
}

// Re-export types for convenience
export type { CustomerData, LoginResponse };
